/*eslint max-lines-per-function: ["error", 215]*/
import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Card, Tab } from 'react-bootstrap';
import { useSearchParams } from 'react-router-dom';

import { useTranslation } from '../../../common/hooks/useTranslation';
import { usePokCore } from '../../../common/hooks/usePokCore';
import {
  GridCountFunction,
  GridGetterFunction,
} from '../../../common/components/Grid/GridDataTypes';
import { ProjectSearch } from '../../components/Projects/ProjectSearch';
import {
  GetProjectDto,
  ResponseError,
} from '../../../common/pokCore/autogenerated/pokApiClient';
import { convert } from '../../../common/pokCore/contexts/ProjectContext';
import { TabsHistory } from '../../../app/components/TabsHistory';
import { validate } from '../../../common/pokCore/contexts/ProjectApplicationContext';
import { responseErrors } from '../../../common/pokCore/validation/responseErrors';
import { ProjectApplicationTypeEnum } from '../../../common/pokCore/validation/enums';

import { ProjectsApplicationDetailsModal } from './ProjectsApplicationDetailsModal';

const PROJECT_APPLICATIONS_TABS = [
  {
    id: 'delayed',
    label: 'delayed',
    isWaiting: true,
    applicationType: ProjectApplicationTypeEnum.Delayed,
  },
  {
    id: 'canceling',
    label: 'canceling',
    isWaiting: true,
    applicationType: ProjectApplicationTypeEnum.ToCancel,
  },
  {
    id: 'datechange',
    label: 'datechange',
    isWaiting: true,
    applicationType: ProjectApplicationTypeEnum.DateChange,
  },
  {
    id: 'allDelayed',
    label: 'allDelayed',
    isWaiting: false,
    applicationType: ProjectApplicationTypeEnum.Delayed,
  },
  {
    id: 'allCanceling',
    label: 'allCanceling',
    isWaiting: false,
    applicationType: ProjectApplicationTypeEnum.ToCancel,
  },
  {
    id: 'allDateChanging',
    label: 'allDateChanging',
    isWaiting: false,
    applicationType: ProjectApplicationTypeEnum.DateChange,
  },
];

export const ProjectsApplicationView: React.FC = () => {
  const { t, tk } = useTranslation('applicationLng');
  const projectLng = useTranslation('projectLng');
  const menuLng = useTranslation('menuLng');
  const pok = usePokCore();

  const [project, setProject] = useState<GetProjectDto>();
  const [show, setShow] = useState<boolean>(false);
  const [refresh, setRefresh] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState('delayed');
  const [companyMismatch, setCompanyMismatch] = useState(false);
  const [linkCompanyName, setLinkCompanyName] = useState<string>();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    setActiveTab(searchParams?.get('activeTab') ?? 'delayed');
  }, [searchParams]);

  useEffect(() => {
    const urlCompanyId = searchParams?.get('companyId');
    if (urlCompanyId && urlCompanyId !== pok.getCompanyId()) {
      setCompanyMismatch(true);
      pok.companies
        .getById(urlCompanyId)
        .then(c => setLinkCompanyName(c.name))
        .catch(() => {});
    } else {
      setCompanyMismatch(false);
    }
  }, [searchParams, pok]);

  const getLoadDataFunction = useMemo(
    () => (applicationType: ProjectApplicationTypeEnum, isWaiting?: boolean) =>
      ((pageSize, pageNumber, orderBy, orderDirection, filterText) => {
        setRefresh(false);
        return (
          isWaiting
            ? pok.projectsApplication.getAllApplicationWaitingByPortion
            : pok.projectsApplication.getAllApplicationByPortion
        )(
          projectLng.t,
          projectLng.tk,
          applicationType,
          pageSize,
          pageNumber,
          orderBy,
          orderDirection,
          filterText,
        );
      }) as GridGetterFunction,
    [pok.projectsApplication, setRefresh, projectLng.t, projectLng.tk],
  );

  const getLoadCountFunction = useMemo(
    () => (applicationType: ProjectApplicationTypeEnum, isWaiting?: boolean) =>
      (filterText =>
        (isWaiting
          ? pok.projectsApplication.getAllApplicationWaitingCount
          : pok.projectsApplication.getAllApplicationCount)(
          applicationType,
          filterText,
        )) as GridCountFunction,
    [pok.projectsApplication],
  );

  const handleClick = async (id: string) => {
    const fetchedProject = await pok.projects.getById(id);
    setProject(fetchedProject);
    setShow(true);
  };

  const handleClose = () => {
    setProject(undefined);
    setShow(false);
  };

  const handleAcceptApplication = useMemo(
    () => (type: ProjectApplicationTypeEnum) => async () => {
      if (project) {
        try {
          await pok.projectsApplication.applicationAccept(type, project.id);
          await pok.emails.sendProjectResponseEmail(project.id, type, true);
          setProject(undefined);
          setShow(false);
          setRefresh(true);
          return { saved: true, errors: [] };
        } catch (response) {
          return {
            saved: false,
            errors: await responseErrors(response as ResponseError),
          };
        }
      } else {
        return {
          saved: false,
          errors: [],
        };
      }
    },
    [pok.emails, pok.projectsApplication, project],
  );

  const handleRejectApplication = useMemo(
    () =>
      (type: ProjectApplicationTypeEnum) =>
      async (rejectedNote?: string) => {
        const status = await validate({ rejectedNote: rejectedNote as string });
        if (project && status.valid) {
          try {
            await pok.projectsApplication.applicationReject(
              type,
              project.id,
              rejectedNote ?? '',
            );

            await pok.emails.sendProjectResponseEmail(project.id, type, false);

            setProject(undefined);
            setShow(false);
            setRefresh(true);
            return {
              saved: true,
              errors: [],
            };
          } catch (response) {
            return {
              saved: false,
              errors: await responseErrors(response as ResponseError),
            };
          }
        } else {
          return {
            saved: false,
            errors: status?.errors || [],
          };
        }
      },
    [pok.emails, pok.projectsApplication, project],
  );

  const readOnly = (applicationType: ProjectApplicationTypeEnum) => {
    if (applicationType === ProjectApplicationTypeEnum.Delayed) {
      return !project?.delayVerification || !project?.active;
    } else if (applicationType === ProjectApplicationTypeEnum.ToCancel) {
      return !project?.cancelVerification || !project?.active;
    } else if (applicationType === ProjectApplicationTypeEnum.DateChange) {
      return !project?.dateChangeVerification || !project?.active;
    }
    return true;
  };

  return (
    <Card className="main-card">
      <Card.Body className="m-3">
        <Card.Title className="pb-3">
          {t(tk.prLang.projectsApplications)}
        </Card.Title>
        {companyMismatch && (
          <Alert variant="warning">
            <b>{menuLng.t(menuLng.tk.system.badCompany)}.</b>{' '}
            {menuLng.t(menuLng.tk.system.applicationLinkRedirectsToCompany)}{' '}
            {linkCompanyName && <b>{linkCompanyName}.</b>}{' '}
            {menuLng.t(menuLng.tk.system.applicationChangeCompanyInfo)}
          </Alert>
        )}
        <TabsHistory
          id="projectApplications"
          activeKey={activeTab}
          setActiveTab={setActiveTab}
          mountOnEnter={true}
          unmountOnExit={true}
        >
          {PROJECT_APPLICATIONS_TABS.map(
            ({ id, label, isWaiting, applicationType }) => (
              <Tab
                key={id}
                eventKey={id}
                title={t(tk.app[label as keyof typeof tk.app])}
                className="m-3"
              >
                <ProjectSearch
                  isDelayed={!isWaiting}
                  getData={getLoadDataFunction(applicationType, isWaiting)}
                  getCount={getLoadCountFunction(applicationType, isWaiting)}
                  onRowClick={handleClick}
                  refresh={refresh}
                  showActionColumn={true}
                  applicationType={applicationType}
                />
                <ProjectsApplicationDetailsModal
                  applicationType={applicationType}
                  rejectedNote={
                    applicationType === ProjectApplicationTypeEnum.DateChange
                      ? project?.dateChangeRejectedNote
                      : project?.rejectedNote
                  }
                  project={convert(project)}
                  purchaserName={project?.purchaser?.name || ''}
                  brandName={project?.brand?.name || ''}
                  show={show}
                  onAccept={handleAcceptApplication(applicationType)}
                  onReject={handleRejectApplication(applicationType)}
                  onClose={handleClose}
                  readOnly={readOnly(applicationType)}
                />
              </Tab>
            ),
          )}
        </TabsHistory>
      </Card.Body>
    </Card>
  );
};
